import { MainHeading } from '@/components/common/MainHeading';
import { Layout } from '@/components/Layout/Layout'
import { PencilIcon, Plus, Search } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react'
import { IVarientListResponse, IVarients, IVarientsResponse } from '@/interfaces/Varient/varient.interface';
import CommonTable, { Action, TableColumn } from '@/components/common/CommonTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { RoutesLink } from '@/utility/RouteLinks';
import { Switch } from '@/components/ui/switch';
import { useFilter } from '@/hooks/useFilter';
import { IFilter, IPageInfo } from '@/interfaces/common.interface';
import { formatRand, GetFilterUrl, showToastNotification } from '@/utility/helper';
import Pagination from '@/components/common/Pagination';
import { TableFilters } from '@/components/common/TableFilters';
import { TableItemIdentity } from '@/components/common/TableItemIdentity';
import { UpdateVarientStockModal } from '@/components/common/Model/UpdateVarientStockModal';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { IProduct, IProductResponse } from '@/interfaces/Product/product.interface';
import { MultiSelect } from '@/components/common/MultiSelect';

export const Varients = () => {
    const navigate = useRoleNavigate();
    const { canCreate, canEdit, canView, hasPermission, isAdmin } = usePermissions();
    const canDo = canEdit(RESOURCES.VARIANT);
    const canAddStock = hasPermission(RESOURCES.VARIANT, 'add-stock');
    const [varients, setVarients] = useState<IVarients[]>([]);
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [stockUpdateVarient, setStockUpdateVarient] = useState<IVarients | null>(null);

    const [products, setProducts] = useState<IProduct[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    const { filters, debouncedFilters, updateFilter, handlePageChange, handlePageSizeChange } = useFilter<IFilter>({
        search: '',
        page: 1,
        limit: 10,
    });

    const [pageInfo, setPageInfo] = useState<IPageInfo>({
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0,
    });


    useEffect(() => {
        fetchVarients();
    }, [debouncedFilters.search, debouncedFilters.page, debouncedFilters.limit, debouncedFilters.productId]);


    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async (search = "") => {
        setLoadingProducts(true);
        try {
            const queryParams = GetFilterUrl({ limit: 50, isGlobal: isAdmin ? "" : "0", search: search });

            const response = await callApi<IProductResponse>(
                `${API_URL + API_ENDPOINT.products}?${queryParams}`,
                { method: 'GET' }
            );
            setProducts(response?.data?.products || []);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoadingProducts(false);
        }
    };


    const fetchVarients = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<IVarientsResponse>(
                `${API_URL + API_ENDPOINT.varients}?${queryParams}`,
                { method: 'GET' }
            );
            setVarients(response?.data?.variants || []);
            setPageInfo(
                response?.data?.pageInfo || {
                    limit: 10, page: 1, total: 0,
                    totalPages: 0
                },
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    }


    const handleStockUpdateSuccess = (variantId: string, newStock: number) => {
        setVarients(prev => prev.map(v => {
            if (v.id === variantId) {
                return { ...v, unit: String(newStock) };
            }
            return v;
        }));
    };

    const handleStatusToggle = async (id: string, currentStatus: string) => {
        setUpdatingId(id);
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            const response = await callApi<any>(`${API_URL}${API_ENDPOINT.varients}/${id}`, {
                method: 'PATCH',
                data: { status: newStatus }
            });
            showToastNotification("success", response?.message || "Status updated successfully");

            // Update local state for immediate feedback
            setVarients(prev => prev.map(v => v.id === id ? { ...v, status: newStatus } : v));
        } catch (error) {
            ThrowError(error);
        } finally {
            setUpdatingId(null);
        }
    };

    const columns: TableColumn<IVarients>[] = [
        {
            key: "product",
            header: "Product",
            render: (item) => (
                <TableItemIdentity
                    image={item.product?.media?.[0]?.url}
                    name={item.product?.name || "N/A"}
                    subtitle={item.product?.category?.name || "N/A"}
                    fallbackText="No Img"
                />
            )
        },
        {
            key: "name",
            header: "Variant",
            render: (item) => (
                <div className="flex flex-col">
                    <span className="font-medium text-foreground">{item.name}</span>
                </div>
            )
        },
        {
            key: "price",
            header: "Price",
            render: (item) => <div className='text-muted-foreground'>
                {formatRand(item.price)}
            </div>
        },
        {
            key: "stockQuantity",
            header: "Stock",
            render: (item) => (
                <div className="flex flex-col">
                    <span className={`font-medium ${item.stockQuantity <= 5 ? 'text-destructive' : 'text-foreground'}`}>
                        {item.unit || 0} Units
                    </span>
                    {Number(item.unit) <= 5 && (
                        <span className="text-[9px] text-destructive font-bold uppercase">Low Stock</span>
                    )}
                </div>
            )
        },
        {
            key: "status",
            header: "Status",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <Switch
                        checked={item.status === 'active'}
                        onCheckedChange={() => handleStatusToggle(item.id, item.status)}
                        disabled={updatingId === item.id || !canDo}
                        aria-label="Toggle status"
                    />
                    <StatusBadge status={item.status} />
                </div>
            )
        }
    ]

    const getActions = (_record: IVarients): Action<IVarients>[] => [
        ...(canDo ? [
            {
                text: "Edit",
                icon: <PencilIcon className='w-4 h-4 cursor-pointer' />,
                onClick: (record: IVarients) => {
                    navigate(RoutesLink.EditVarients.replace(':id', record.id));
                }
            }
        ] : []),
        ...(canAddStock ? [
            {
                text: "Add Stock",
                icon: <Plus className='w-4 h-4 cursor-pointer' />,
                onClick: (record: IVarients) => {
                    setStockUpdateVarient(record);
                }
            }
        ] : [])
    ]

    return (
        <Layout>
            <MainHeading
                title="Varients"
                description={`${pageInfo.total} Varients Total · Active Configuration`}
                actionLabel={canCreate(RESOURCES.VARIANT) ? "Add Varients" : ""}
                actionIcon={<Plus className="w-4 h-4" />}
                onAction={() => {
                    navigate(RoutesLink.AddVarients);
                }}
            >
            </MainHeading>

            <div className="mt-6 flex flex-col gap-4">
                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val, page: 1 })}
                    placeholder="Search variants..."
                >
                    <div className="w-64">
                        <MultiSelect
                            options={products.map(p => ({ label: p.name, value: p.id }))}
                            selected={filters.productId ? [filters.productId] : []}
                            onChange={(vals) => updateFilter({ productId: vals[0] || "", page: 1 })}
                            onSearchChange={fetchProducts}
                            placeholder="Select Product"
                            singleSelect
                            loading={loadingProducts}
                        />
                    </div>
                </TableFilters>
                <CommonTable
                    columns={columns}
                    data={varients}
                    loading={loading}
                    emptyMessage="No Varients found. Try adding one!"
                    actions={getActions}
                />

                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageInfo.totalPages}
                    totalItems={pageInfo.total}
                    itemsPerPage={pageInfo.limit}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            <UpdateVarientStockModal
                isOpen={!!stockUpdateVarient}
                variant={stockUpdateVarient}
                onClose={() => setStockUpdateVarient(null)}
                onSuccess={handleStockUpdateSuccess}
            />
        </Layout>
    )
}
